import { User } from './action-types';
import history from '../history';
import { USER_STUB } from '../tests/fixtures/stubs/user';
import { showFeedback } from './ui';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../core/net';
import { Dispatch, Action } from 'redux';
import {
  AmplifyCore,
  authenticationContext,
  commonEnums,
  HttpMethods
} from '@codametrix/ui-common';

const { paginationUtils } = AmplifyCore;

const { FilterTypes } = commonEnums;
/** The sync action creators */
const userList = createAction<CMxAPI.PageableList<CMxAPI.User>>(User.LIST);
const userSelected = createAction<CMxAPI.User>(User.USER_SELECTED);
const updateRole = createAction<CMxAPI.User>(User.ROLE_UPDATE);
const availableRoles = createAction<CMxAPI.SystemRole[]>(User.AVAILABLE_ROLES);
const availableOrgs = createAction<CMxAPI.Organization>(
  User.AVAILABLE_ORGANIZATIONS
);
const formSubmissionError = createAction<CMxCommonApp.FormErrors>(
  User.FORM_SUBMISSION_ERROR
);
const addUserDetails = createAction<CMxAPI.User>(User.ADD_USER_DETAILS);

const toggleForcePasswordOption = createAction<boolean>(
  User.TOGGLE_PASSWORD_OPTIONS
);

const togglePasswordChangeModal = createAction<boolean>(
  User.TOGGLE_PASSWORD_CHANGE_MODAL
);

const toggleFilterOn = createAction<boolean>(User.TOGGLE_FILTER_ON);
const toggleShowErrors = createAction<boolean>(User.TOGGLE_SHOW_ERRORS);

/** async actions */

const list = createAsyncThunk<
  CMxAPI.PageableList<CMxAPI.User>,
  CMxCommonApp.SortablePageable<CMxAPI.User>,
  { rejectValue: CMxCommonApp.SubmitError }
>(User.LIST, async (params, { dispatch }) => {
  const sortablePageable = params;

  const searchParams = paginationUtils.searchParamsFactory(sortablePageable);

  let body: any[] = [];

  const filters = sortablePageable.filterableOptions.filters;

  if (filters) {
    filters.forEach(filter => {
      body.push(filter);
    });
  } else {
    body.push({
      key: 'role',
      terms: ['true'],
      type: FilterTypes.IN
    });
  }

  const users = await api<CMxAPI.PageableList<CMxAPI.User>>({
    endpoint: `/users/list/v1?${searchParams.toString()}`,
    init: {
      method: HttpMethods.POST
    },
    body: body
  });

  dispatch(userList(users) as any);
  return users;
});

type SaveArgs = {
  token: CMxCommonApp.BearerToken;
  user: CMxAPI.User;
};

const updateUser = createAsyncThunk<
  CMxAPI.User,
  SaveArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(User.ROLE_UPDATE, async (params, { dispatch }) => {
  const { user } = params;
  dispatch(updateRole(user) as any);
  return user;
});

/** The asynchronous login action; Error type is optional */
const save = createAsyncThunk<
  CMxAPI.User,
  SaveArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>('Save User', async (params, { dispatch }) => {
  const { user, token } = params;
  const isUpdate = user.id !== null;
  const method: string = isUpdate ? HttpMethods.PUT : HttpMethods.POST;
  const endpoint: string = isUpdate
    ? `/users/${user.userId}/v1`
    : `/users/v1`;

  const userDetails = await api<CMxAPI.User>({
    endpoint,
    init: { method },
    headers: {
      Authorization: token.token
    },
    body: user
  });

    history.push('/cmx/admin/users/');

  dispatch(
    showFeedback({
      id: Date.now(),
      message: `Saved information for ${userDetails.firstName} ${userDetails.lastName}.`,
      dismissable: true
    }) as any
  );

    return userDetails;
  }
);

const dispatchOrgAndRoles = async (
  dispatch: Dispatch<Action>,
  orgContext: CMxAPI.OrganizationContext
) => {
  const org = await api<CMxAPI.Organization>({
    endpoint: `/organization/id/v1?organizationId=${orgContext.organizationId}`
  });
  dispatch(availableOrgs(org) as any);

  const roles = await api<CMxAPI.SystemRole[]>({
    endpoint: `/roles/v1/`
  });
  dispatch(availableRoles(roles) as any);
};

const addUser = createAsyncThunk<
  void,
  CMx.AddUserArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>('Add User', async (params, { dispatch }) => {
  const { orgContext } = params;
  dispatch(userSelected(USER_STUB) as any);
  history.push('/cmx/admin/users/new/');
  await dispatchOrgAndRoles(dispatch as any, orgContext);
});

const getDetail = createAsyncThunk<
  CMxAPI.User,
  CMx.UserArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(User.USER_SELECTED, async (params, { dispatch }) => {
  const { user, orgContext } = params;
  const userDetails = await api<CMxAPI.User>({
    endpoint: `/users/${user.userId}/v1`
  });
  dispatch(userSelected(userDetails) as any);
  history.push(`/cmx/admin/users/${userDetails.id}/`) as any;
  dispatchOrgAndRoles(dispatch as any, orgContext);

  return userDetails;
});

const getBasicUserDetail = createAsyncThunk<
  Pick<CMxAPI.User, 'username' | 'firstName' | 'lastName'>,
  { userId: string },
  { rejectValue: CMxCommonApp.SubmitError }
>('USER_BASIC_INFO_FETCHED', async (params, { dispatch }) => {
  const { userId } = params;

  const fullUser = await api<CMxAPI.User>({
    endpoint: `/users/${userId}/v1`
  });

  const { username, firstName, lastName } = fullUser;

  return { username, firstName, lastName };
});

const changeUsersPassword = createAsyncThunk<
  void,
  CMx.ChangeUserPasswordData,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  User.CHANGE_USERS_PASSWORD,
  async (userData, { dispatch }): Promise<void> => {
    const { selectedUser, ...passwords } = userData;

    const adminToken = authenticationContext.getToken();

    await api<CMxAPI.User>({
      endpoint: `/users/${selectedUser.userId}/v1`,
      init: { method: HttpMethods.PUT },
      headers: {
        Authorization: adminToken
      },
      body: {
        ...selectedUser,
        forcePasswordChange: true,
        password: passwords.newPassword,
        confirmPassword: passwords.newPasswordConfirm
      }
    });
  }
);

export const actions = {
  list,
  userList,
  userSelected,
  updateRole,
  availableRoles,
  availableOrgs,
  formSubmissionError,
  save,
  addUser,
  addUserDetails,
  toggleForcePasswordOption,
  togglePasswordChangeModal,
  changeUsersPassword,
  toggleFilterOn,
  toggleShowErrors
};
export {
  getDetail,
  getBasicUserDetail,
  updateUser,
  addUser,
  save,
  list,
  addUserDetails,
  toggleForcePasswordOption,
  togglePasswordChangeModal,
  changeUsersPassword,
  toggleFilterOn,
  toggleShowErrors
};
